import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, LinearProgress, Link, MenuItem, Stack, TextField, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { Pill, SectionCard, GapNote, NoteInfoIcon, helperProps } from './common'
import { RateCardHeatmap, HeatLegend } from './Tables'
import WorkingFilesCard from './WorkingFilesCard'
import CounterEditorDialog from './CounterEditorDialog'
import ReturnDeclineDialog from './ReturnDeclineDialog'
import ProspectAndCommercialSummary from './ProspectAndCommercialSummary'
import { PATHS, BUCKETS, ZONE_CLASSES } from '../constants'
import { openS3Doc } from '../s3Doc'
import {
  useGetVersionQuery, useGetVersionsQuery, useGetProposalQuery,
  useApproveMutation, useEscalateMutation, useReturnProposalMutation, useDeclineMutation, useCounterMutation,
  useGetCommentsQuery, usePostCommentMutation, usePostCommentAttachmentMutation,
} from '../../../redux/d2cApiSlice'
import {
  lanesToHeatmap, versionThreadRows, approvalChainRows, cardLabel,
  BUCKET_LABEL_TO_KEY, serviceLabel, laneShipments,
} from '../apiMap'

// Shared review-surface building blocks (command header, action deck, numbered titles,
// tinted RFI matrices) — one source with ProposalDetailModal so the two never drift.
import { cols, miniGrid, SecTitle, onBluePill, statLbl, statVal, statSub, btnPri, btnBlue, btnGhost, btnDanger, ActionGroup, ROLE_CHIP, RfiMatrix, rfiMatricesFromVersion } from './reviewBits'

/**
 * DealReview — shared review workspace for L1 and FH.
 * role: 'l1' | 'fh'. versionId/proposalId/brand/card come from the queue row
 * via navigation state (see L1DealReview / FHDealReview).
 */
export default function DealReview({ role = 'l1', versionId, proposalId, brand, card, orderDataFileUri, status = null }) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [counter, setCounter] = useState({ open: false, mode: 'ui' })
  const [rd, setRd] = useState({ open: false, variant: 'return-sl' })

  const isFh = role === 'fh'
  const title = isFh ? 'Deal Review · Functional Head' : 'Deal Review'
  const queuePath = isFh ? PATHS.fhQueue : PATHS.l1Queue

  // Auto-refresh: when on, poll the version + thread every 15s (real RTK Query polling).
  const [autoRefresh, setAutoRefresh] = useState(false)
  const pollingInterval = autoRefresh ? 15000 : 0
  const { data: version, isLoading, isError, error } = useGetVersionQuery(versionId, { skip: !versionId, pollingInterval })
  const { data: versions } = useGetVersionsQuery(proposalId, { skip: !proposalId, pollingInterval })
  // Brand/HubSpot/revenue-potential live on the proposal, not the version — the
  // queue row's navigation state only carries `brand`, so fetch the rest for the
  // full Prospect Details summary below.
  const { data: deal } = useGetProposalQuery(proposalId, { skip: !proposalId })

  const [approve, approveState] = useApproveMutation()
  const [escalate, escalateState] = useEscalateMutation()
  const [returnProposal, returnState] = useReturnProposalMutation()
  const [decline, declineState] = useDeclineMutation()
  const [counterMut, counterState] = useCounterMutation()
  const rdBusy = escalateState.isLoading || returnState.isLoading || declineState.isLoading

  // ── Comments & attachments (v2 §1) — real, deal-scoped ──────────────────────
  const { data: comments = [] } = useGetCommentsQuery(proposalId, { skip: !proposalId })
  const [postComment, postState] = usePostCommentMutation()
  const [postAttachment, attachState] = usePostCommentAttachmentMutation()
  const [cText, setCText] = useState('')
  const [cKind, setCKind] = useState('comment')
  const [cFile, setCFile] = useState(null)
  const [cTextErr, setCTextErr] = useState(false)
  const commentBusy = postState.isLoading || attachState.isLoading
  const doPostComment = async () => {
    if (!cText.trim()) { setCTextErr(true); return }
    try {
      const created = await postComment({ proposalId, text: cText.trim(), kind: cKind }).unwrap()
      if (cFile && created?.id) {
        const fd = new FormData(); fd.append('file', cFile)
        await postAttachment({ proposalId, commentId: created.id, formData: fd }).unwrap()
      }
      setCText(''); setCFile(null); setCKind('comment')
      setCTextErr(false)
      enqueueSnackbar('Comment posted.', { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Could not post comment.', { variant: 'error' }) }
  }

  const heatmap = useMemo(() => lanesToHeatmap(version?.computedLanes || []), [version])
  const threadRows = useMemo(() => versionThreadRows(versions || [], versionId), [versions, versionId])
  const auditRows = useMemo(() => approvalChainRows(version || {}), [version])
  const exceptions = version?.capturedInputs?.exceptions || []
  // Standard (GTM) mode: pre-built cards handed over as-is (no pricing ran for these).
  // Stashed on capturedInputs at generate time, keyed by API serviceType.
  const standardCards = version?.capturedInputs?.standardCards || {}
  const standardCardRows = Object.entries(standardCards).map(([st, c]) => ({ serviceType: st, ...c }))
  // Order-level RFI the SL supplied — pivoted per service into the same slab × zone
  // matrix shape the SL entered it in (and the same shape as the margin heatmap above),
  // instead of the old flat one-row-per-service×zone×slab list, which at 10 slabs ×
  // 5 zones × 2 services ran to 100 rows.
  const rfiMatrices = useMemo(() => rfiMatricesFromVersion(version), [version])
  const pickupRows = useMemo(() => (version?.pickupLocations || []).map((p, i) => ({
    _k: i, pin: p.pincode, zone: p.zoneTag, vol: (p.monthlyVolume ?? 0).toLocaleString('en-IN'),
  })), [version])
  // The heatmap says WHERE margin is low; this says where it MATTERS — the cells pulling
  // the deal margin down the most: below-deal-margin cells ranked by
  // (deal margin − cell margin) × the cell's share of total shipments.
  const dragRows = useMemo(() => {
    const lanes = (version?.computedLanes || []).filter((l) => l.marginPct != null)
    if (!lanes.length) return []
    const cells = {}
    lanes.forEach((l) => {
      const key = `${l.serviceType}|${l.zone}|${l.weightSlab}`
      const c = cells[key] || (cells[key] = { id: key, serviceType: l.serviceType, zone: l.zone, slab: l.weightSlab, ship: 0, mSum: 0 })
      const s = laneShipments(l)
      c.ship += s; c.mSum += l.marginPct * s
    })
    const arr = Object.values(cells).filter((c) => c.ship > 0)
    const tot = arr.reduce((s, c) => s + c.ship, 0)
    if (!tot) return []
    const dealM = version?.dealMarginPct
    return arr
      .map((c) => ({ ...c, margin: c.mSum / c.ship, share: (c.ship / tot) * 100 }))
      .filter((c) => dealM == null || c.margin < dealM)
      .map((c) => ({ ...c, drag: ((dealM ?? 15) - c.margin) * c.share }))
      .sort((a, b) => b.drag - a.drag)
      .slice(0, 5)
      .map((c) => ({
        ...c,
        service: serviceLabel(c.serviceType),
        marginTxt: `${Math.round(c.margin * 10) / 10}%`,
        shareTxt: `${Math.round(c.share * 10) / 10}%`,
      }))
  }, [version])
  // The FH's return remark, surfaced right in the RETURNED_TO_L1 flag instead of
  // "see the thread below" (a full-page scroll away). Chain remarks first (that's
  // where Return's remarks land), latest comment as fallback.
  const latestReturnRemark = useMemo(() => {
    const chain = [...(version?.approvalChain || [])].reverse().find((s) => s.remarks)
    if (chain) return { who: chain.userName || chain.level || '—', text: chain.remarks }
    const c = (comments || []).reduce((best, x) => (!best || new Date(x.createdAt || 0) > new Date(best.createdAt || 0) ? x : best), null)
    return c ? { who: c.authorRole || '—', text: c.text } : null
  }, [version, comments])
  const sectionDefs = useMemo(
    () => (version?.services || []).map((s, i) => ({ id: i, name: serviceLabel(s.serviceType), serviceType: s.serviceType })),
    [version],
  )
  const cardTxt = card || cardLabel(version?.rateCardMode, (version?.services || []).map((s) => s.serviceType))
  const marginTxt = version?.dealMarginPct == null ? 'margin masked' : `${version.dealMarginPct}%`

  const doApprove = async () => {
    try {
      await approve(versionId).unwrap()
      enqueueSnackbar(isFh ? 'Approved & released — final. Sharing unlocked; sales leader notified.' : 'Approved & released. Sharing unlocked; Functional Head informed.', { variant: 'success' })
      navigate(queuePath)
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Approve failed.'
      // Any FH-role user can act at the FH step (role-resolved); "not eligible" now
      // only means an L1 step assigned to a different designated L1 (slRoutingMap).
      enqueueSnackbar(/not eligible/i.test(msg)
        ? `${msg} — L1 review is routed 1:1 per Sales Leader (slRoutingMap), frozen when the deal was generated. If the routing config changed since, the SL must re-generate the deal.`
        : msg, { variant: 'error' })
    }
  }

  // ReturnDeclineDialog hands back {remarks, brandRfiRequired, reason}; map by variant.
  const submitRd = async ({ remarks, brandRfiRequired, reason }) => {
    const v = rd.variant
    try {
      if (v === 'escalate') {
        await escalate({ versionId, body: { note: remarks || undefined } }).unwrap()
        enqueueSnackbar('Escalated to Functional Head.', { variant: 'info' })
      } else if (v === 'decline') {
        await decline({ versionId, body: { lossReason: reason, note: remarks || undefined } }).unwrap()
        enqueueSnackbar('Deal declined — logged as lost.', { variant: 'warning' })
      } else {
        // return-sl (L1→SL: no destination; FH→SL: destination 'SL') | return-l1 (FH→L1)
        const body = { remarks, brandRfiRequired }
        if (v === 'return-l1') body.destination = 'L1'
        else if (v === 'return-sl' && isFh) body.destination = 'SL'
        await returnProposal({ versionId, body }).unwrap()
        enqueueSnackbar('Returned with remarks.', { variant: 'info' })
      }
      setRd({ ...rd, open: false })
      navigate(queuePath)
    } catch (e) {
      // RTK Query puts the server's message on e.data.message (BUG-02: it was silently
      // dropped here, so escalate/decline/return failures showed the generic fallback).
      enqueueSnackbar(e?.data?.message || e?.message || 'Action failed.', { variant: 'error' })
    }
  }

  // CounterEditorDialog hands back {mode, destination, brandRfiRequired, sections, note}.
  const submitCounter = async ({ mode, destination, brandRfiRequired, sections, note }) => {
    try {
      await counterMut({ versionId, body: { mode, destination, brandRfiRequired, sections } }).unwrap()
      // The optional version label/note persists for real on the deal's comment thread.
      if (note) { try { await postComment({ proposalId, text: `Counter note: ${note}`, kind: 'comment' }).unwrap() } catch { /* comment is best-effort */ } }
      enqueueSnackbar('Counter submitted.', { variant: 'success' })
      setCounter({ ...counter, open: false })
      navigate(queuePath)
    } catch (e) {
      const msg = e?.data?.message || e?.message || 'Counter failed.'
      let friendly = msg
      if (/Route 3 not allowed/i.test(msg)) friendly = 'These countered rates take the deal below 10% (Route 3) — a below-10% counter can’t go back to the Sales Leader. Raise the rates, or send the counter to the Functional Head instead.'
      else if (/margin must be >= 0/i.test(msg)) friendly = 'The counter’s margin is negative — raise the rates to at least break-even before submitting.'
      else if (/not eligible/i.test(msg)) friendly = `${msg} — routing is 1:1 per Sales Leader (slRoutingMap), frozen when the deal was generated. If the routing config changed since, the SL must re-generate the deal.`
      enqueueSnackbar(friendly, { variant: 'error' })
    }
  }

  // ④'s tables borrow the forward-shipments table's language: tinted chips and
  // coloured keywords per cell instead of a wall of identical grey text.
  const versionCols = cols([
    {
      field: 'v', headerName: 'Version', minWidth: 130, flex: 0.8,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{String(row.v).replace(' · current', '')}</Typography>
          {row.current && <Box component="span" sx={{ fontSize: 10, fontWeight: 700, px: 0.9, py: 0.25, borderRadius: '9px', bgcolor: '#EAF1FC', color: '#103A75', border: '1px solid #CFE0F7' }}>current</Box>}
        </Stack>
      ),
    },
    {
      field: 'by', headerName: 'Created by', minWidth: 110, flex: 0.6,
      renderCell: ({ row }) => {
        const c = ROLE_CHIP[row.by] || { bg: '#F3F4F6', fg: '#4B5563' }
        return <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1.1, py: 0.3, borderRadius: '10px', bgcolor: c.bg, color: c.fg }}>{row.by}</Box>
      },
    },
    {
      field: 'change', headerName: 'What changed', minWidth: 180, flex: 1.3,
      renderCell: ({ row }) => {
        const up = /\+/.test(row.change)
        const down = /−|-\d/.test(row.change)
        const tone = row.change === 'First version' ? '#4B5563' : up ? '#15803D' : down ? '#B91C1C' : 'inherit'
        return <Typography sx={{ fontSize: 12.5, fontWeight: row.change === '—' ? 400 : 700, color: tone }}>{row.change}</Typography>
      },
    },
    {
      field: 'margin', headerName: 'Margin', minWidth: 90, align: 'right', headerAlign: 'right',
      renderCell: ({ row }) => (row.margin === 'masked'
        ? <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>masked</Typography>
        : <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{row.margin}</Typography>),
    },
    {
      field: 'route', headerName: 'Route', minWidth: 100,
      renderCell: ({ row }) => {
        const n = parseInt(String(row.route).replace(/\D/g, ''), 10)
        const c = n === 1 ? { bg: '#ECFDF5', fg: '#15803D' } : n === 2 ? { bg: '#FEF9C3', fg: '#A16207' } : n === 3 ? { bg: '#FEE2E2', fg: '#B91C1C' } : { bg: '#F3F4F6', fg: '#4B5563' }
        return <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1.1, py: 0.3, borderRadius: '10px', bgcolor: c.bg, color: c.fg }}>{row.route}</Box>
      },
    },
    {
      field: 'reached', headerName: 'Band', minWidth: 120,
      renderCell: ({ row }) => {
        const bk = BUCKET_LABEL_TO_KEY[row.reached]
        return bk ? <Pill label={row.reached} bucketKey={bk} /> : <Pill label={row.reached} styleKey="neutral" />
      },
    },
    { field: 'when', headerName: 'When', minWidth: 150, flex: 1, renderCell: ({ row }) => <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{row.when}</Typography> },
  ])
  const auditCols = cols([
    { field: 'when', headerName: 'When', minWidth: 150, flex: 0.8, renderCell: ({ row }) => <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{row.when}</Typography> },
    {
      field: 'who', headerName: 'Who', minWidth: 110, flex: 0.6,
      renderCell: ({ row }) => {
        const c = ROLE_CHIP[row.who] || null
        return c
          ? <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1.1, py: 0.3, borderRadius: '10px', bgcolor: c.bg, color: c.fg }}>{row.who}</Box>
          : <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{row.who}</Typography>
      },
    },
    {
      field: 'action', headerName: 'Action', minWidth: 240, flex: 1.8,
      renderCell: ({ row }) => {
        const [status, ...rest] = String(row.action).split(' — ')
        const c = /APPROVED/i.test(status) ? { bg: '#ECFDF5', fg: '#15803D' }
          : /(REJECT|RETURN|DECLIN)/i.test(status) ? { bg: '#FEE2E2', fg: '#B91C1C' }
          : /(PENDING|ASSIGN|AWAIT)/i.test(status) ? { bg: '#FEF9C3', fg: '#A16207' }
          : { bg: '#F3F4F6', fg: '#4B5563' }
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1.1, py: 0.3, borderRadius: '10px', bgcolor: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>{status}</Box>
            {!!rest.length && <Typography noWrap sx={{ fontSize: 12.5 }}>{rest.join(' — ')}</Typography>}
          </Stack>
        )
      },
    },
  ])
  const pickupCols = cols([
    { field: 'pin', headerName: 'Pickup PIN', minWidth: 110, flex: 0.6, renderCell: ({ row }) => <b>{row.pin}</b> },
    {
      field: 'zone', headerName: 'Zone', minWidth: 100, flex: 0.6,
      renderCell: ({ row }) => {
        const c = ZONE_CLASSES[row.zone] || ZONE_CLASSES.Standard
        return <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1.1, py: 0.3, borderRadius: '10px', bgcolor: c.bg, color: c.color }}>{row.zone || '—'}</Box>
      },
    },
    { field: 'vol', headerName: 'Monthly volume', minWidth: 130, flex: 0.6, align: 'right', headerAlign: 'right', renderCell: ({ row }) => <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{row.vol}</Typography> },
  ])
  const dragCols = cols([
    { field: 'service', headerName: 'Service', minWidth: 130, flex: 1, renderCell: ({ row }) => <b>{row.service}</b> },
    { field: 'zone', headerName: 'Zone', minWidth: 100, flex: 0.7 },
    { field: 'slab', headerName: 'Weight slab', minWidth: 100, flex: 0.7 },
    { field: 'marginTxt', headerName: 'Margin', minWidth: 90, align: 'right', headerAlign: 'right', renderCell: ({ row }) => <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: row.margin < 10 ? '#B42318' : '#B4650A' }}>{row.marginTxt}</Typography> },
    { field: 'shareTxt', headerName: 'Share of shipments', minWidth: 140, align: 'right', headerAlign: 'right' },
  ])

  if (!versionId) {
    return (
      <Box sx={{ px: 3 }}>
        <PageTitle>{title}</PageTitle>
        <GapNote id="review_no_deal" />
        <Button variant="outlined" sx={btn} onClick={() => navigate(queuePath)}>← Back to queue</Button>
      </Box>
    )
  }

  const busy = approveState.isLoading || counterState.isLoading
  // Per Decision Flow v7 + mockup v19 (and backend T8): only a service rate card below
  // 10% (Borderline/Loss) forces FH sign-off. guardrail_breach / restricted_courier are
  // review flags for L1 — approve stays available; L1 may still escalate.
  const EX_LABEL = {
    guardrail_breach: 'a lane is below the margin guardrail',
    service_card_below_10pct: 'a service rate card is below 10% margin',
    restricted_courier: 'a restricted courier was requested',
  }
  const approveBlocked = !isFh && exceptions.includes('service_card_below_10pct')
  // Name the culprit service(s) — reviewers see per-service margins unmasked.
  const lowServices = (version?.services || [])
    .filter((s) => s.marginPct != null && s.marginPct < 10)
    .map((s) => `${serviceLabel(s.serviceType)} (${Math.round(s.marginPct * 10) / 10}%)`)
  const approveBlockReason = lowServices.length
    ? `${EX_LABEL.service_card_below_10pct}: ${lowServices.join(', ')}`
    : EX_LABEL.service_card_below_10pct
  const reviewFlags = exceptions.filter((ex) => ex !== 'service_card_below_10pct').map((ex) => EX_LABEL[ex] || ex)

  // ── State-machine gating (per the backend's state-machine reference §4, updated
  // for the Aug-2026 loss-deal flow, QA #6/#7) ───────────────────────────────────
  // Route 3 (sub-10%): L1 gets Counter (→SL or FH), Return AND Escalate — everything
  // except Approve, which is FH-reserved. Escalate is the L1's primary action there.
  // RETURNED_TO_L1: the only forward action is Counter (→FH).
  // PUSHED_TO_PRICING_AWAITING_ORDER_DATA: read-only for reviewers.
  const route3 = version?.route === 3
  const isPushed = status === 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA'
  const isReturnedL1 = status === 'RETURNED_TO_L1'
  const canApprove = !isPushed && !isReturnedL1 && (isFh || !route3)
  const canEscalate = !isFh && !isPushed && !isReturnedL1
  const canReturn = !isPushed && !isReturnedL1
  const canCounter = !isPushed
  // Sub-10% + L1: escalation to FH is the forward path — promote it to the DECIDE slot.
  const escalateIsPrimary = route3 && !isFh && canEscalate

  // Same band-colour verdict banner Step 4 (the SL's own Review & Next Steps) already
  // uses — one consistent way of showing "what band did this land in" everywhere.
  const bucketKey = version?.dealBucket ? BUCKET_LABEL_TO_KEY[version.dealBucket] : null
  const bucket = bucketKey ? BUCKETS[bucketKey] : null
  // Flag tones reused from the wizard's own status-alert language (TierBuilder's
  // completion alert) instead of raw MUI Alert colours, so every "heads up" on this
  // page reads the same way as the rest of the D2C pricing UI.
  const FLAG_TONE = {
    info: { bg: '#EAF1FC', bd: '#CFE0F7', fg: '#1E4C8A' },
    warn: { bg: '#FBF1E2', bd: '#F0D6A8', fg: '#B4650A' },
    err: { bg: '#FDECEA', bd: '#F3C6C0', fg: '#B42318' },
  }
  const Flag = ({ tone, children }) => {
    const t = FLAG_TONE[tone]
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.75, py: 1.1, borderRadius: '9px', bgcolor: t.bg, border: '1px solid', borderColor: t.bd, color: t.fg, fontSize: 12.5, lineHeight: 1.55, mb: 1.25, '&:last-child': { mb: 0 } }}>
        {children}
      </Box>
    )
  }

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>{title}</PageTitle>
      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load the version.'}</Alert>}

      {/* ── Command header — one card: gradient band (identity + verdict) on top,
          white action deck (grouped buttons) attached beneath. ─────────────── */}
      <Box sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(32,101,209,0.25)', mb: 2 }}>
      <Box sx={{ background: 'linear-gradient(90deg, #103A75, #2065D1)', px: 2.75, py: 2.5, color: '#fff' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.25, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 220 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>{brand || version?.dealId || '—'}</Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {cardTxt && <Pill label={cardTxt} sx={onBluePill} />}
              <Pill label={`v${version?.seq ?? '—'} · Sales Leader`} sx={onBluePill} />
              {/* Per-service margins — which service drags the deal, at a glance. */}
              {(version?.services || []).filter((s) => s.marginPct != null).map((s) => (
                <Pill key={s.serviceType} label={`${serviceLabel(s.serviceType)} · ${Math.round(s.marginPct * 10) / 10}%`} sx={onBluePill} />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography sx={statLbl}>Margin band</Typography>
            <Typography sx={statVal}>{bucket ? bucket.label.split(' ')[0] : (version?.dealBucket || '—')} · {marginTxt}</Typography>
            {bucket && <Typography sx={statSub}>{bucket.range}</Typography>}
          </Box>
          {version?.route != null && (
            <Box>
              <Typography sx={statLbl}>Route</Typography>
              <Typography sx={statVal}>Route {version.route}</Typography>
              <Typography sx={statSub}>{version.route === 1 ? 'L1 sign-off final' : version.route === 2 ? 'L1 → Functional Head' : 'below 10% — only FH can approve'}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      {/* Approved "Option G" — the white action deck: buttons grouped by intent in
          labelled clusters (DECIDE / REWORK / SEND BACK), never one flat line. */}
      <Box sx={{ bgcolor: 'background.paper', px: 2.75, py: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}>
        {canApprove && (
          <ActionGroup label="Decide" tone="decide">
            {/* Disabled MUI buttons swallow pointer events, so a title on the Button itself
                never shows — the wrapping span carries the "why is this blocked" tooltip. */}
            <Box component="span" title={approveBlocked ? `Always-to-FH exception: ${approveBlockReason}` : undefined}>
              <Button sx={btnPri} disabled={busy || approveBlocked} onClick={doApprove}>
                ✓&nbsp;&nbsp;{isFh ? 'Approve & release — final' : 'Approve & release as submitted'}
              </Button>
            </Box>
          </ActionGroup>
        )}
        {/* Sub-10% deal at L1 (Aug-2026 loss-deal flow): Approve is FH-reserved, so the
            forward action IS escalation — it takes the DECIDE slot as the primary. */}
        {escalateIsPrimary && (
          <ActionGroup label="Decide" tone="decide">
            <Button sx={btnPri} disabled={busy} onClick={() => setRd({ open: true, variant: 'escalate' })}>
              ↑&nbsp;&nbsp;Escalate to FH for approval
            </Button>
          </ActionGroup>
        )}
        {canCounter && (
          <ActionGroup label="Rework" tone="rework">
            <Button sx={btnBlue} disabled={busy} onClick={() => setCounter({ open: true, mode: 'ui' })}>Create counter</Button>
            <Button sx={btnBlue} disabled={busy} onClick={() => setCounter({ open: true, mode: 'upload' })}>Upload counter</Button>
          </ActionGroup>
        )}
        {(canEscalate || canReturn || (isFh && !isPushed)) && (
          <ActionGroup label="Send back" tone="send">
            {canEscalate && !escalateIsPrimary && <Button sx={btnGhost} disabled={busy} onClick={() => setRd({ open: true, variant: 'escalate' })}>Escalate to FH</Button>}
            {isFh && !isPushed && <Button sx={btnGhost} disabled={busy} onClick={() => setRd({ open: true, variant: 'return-l1' })}>Return to L1</Button>}
            {canReturn && <Button sx={btnGhost} disabled={busy} onClick={() => setRd({ open: true, variant: 'return-sl' })}>Return to Sales Leader</Button>}
            {isFh && !isPushed && <Button sx={btnDanger} disabled={busy} onClick={() => setRd({ open: true, variant: 'decline' })}>Decline</Button>}
          </ActionGroup>
        )}
        <Typography sx={{ ml: 'auto', alignSelf: 'center', fontSize: 12, color: 'text.secondary' }}>
          {isFh
            ? 'You are the final authority — nothing below 10% ships without your signature.'
            : '⏱ 30-minute review promise · your sign-off is mandatory before sharing'}
        </Typography>
      </Box>
      </Box>

      {/* ── Status flags — only the ones that actually apply render ─────────── */}
      {(isPushed || (isReturnedL1 && !isFh) || (route3 && !isFh && !isPushed && !isReturnedL1) || approveBlocked || (!isFh && reviewFlags.length > 0)) && (
        <Box sx={{ mb: 2 }}>
          {isPushed && (
            <Flag tone="info"><b>Awaiting order data upload from the Sales Leader</b>&nbsp;— this Route-3 deal is read-only until the order-level file arrives; it then lands in the L1 queue for a rebuild.</Flag>
          )}
          {isReturnedL1 && !isFh && (
            <Flag tone="info"><span><b>Returned to you by the Functional Head</b> — the only forward action is a revised <b>Counter to the FH</b>.{latestReturnRemark
              ? <> Their note ({latestReturnRemark.who}): <b>“{latestReturnRemark.text}”</b></>
              : ' See the return remarks in the thread below.'}</span></Flag>
          )}
          {route3 && !isFh && !isPushed && !isReturnedL1 && (
            <Flag tone="info"><b>Route 3 (below 10%)</b>&nbsp;— only the Functional Head can approve this margin. <b>Escalate to FH</b> to move it forward, or <b>Counter / Return</b> to the Sales Leader to rework it.</Flag>
          )}
          {approveBlocked && (
            <Flag tone="err"><b>Functional-Head sign-off required — you can’t self-approve this version:</b>&nbsp;{approveBlockReason} (a service in Borderline/Loss needs FH even if the deal is Healthy). Escalate or create a counter.</Flag>
          )}
          {!isFh && reviewFlags.length > 0 && (
            <Flag tone="warn"><b>Flagged for your review:</b>&nbsp;{reviewFlags.join(' · ')}. You can still approve — or escalate to the Functional Head if it looks off.</Flag>
          )}
        </Box>
      )}

      {/* ── ① Evidence — the actual numbers behind the verdict above ─────────── */}
      <SectionCard
        title={<SecTitle n={1}>Margin per weight slab × zone<NoteInfoIcon id="heatmap_aggregate" /></SecTitle>}
      >
        <RateCardHeatmap data={heatmap} />
        <HeatLegend />
        {!!dragRows.length && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
              Lanes pulling the deal margin down the most
              <NoteInfoIcon body="Cells below the deal margin, ranked by (deal margin − cell margin) × the cell's share of total shipments — a low margin only matters where real volume flows." />
            </Typography>
            {miniGrid(dragCols, dragRows)}
          </Box>
        )}
        {!!exceptions.length && (
          <Box sx={{ mt: 1.5 }}>
            <Flag tone="warn">
              <b>Exceptions on this version:</b>&nbsp;{exceptions.join(' · ')}
              {exceptions.includes('service_card_below_10pct')
                ? ' — a service below 10% needs Functional-Head sign-off; counter or escalate.'
                : ' — flagged for review; you can approve or escalate.'}
            </Flag>
          </Box>
        )}
      </SectionCard>

      {/* ── ② The ask — everything the SL filled in Steps 1 & 3, unmasked, in ONE
          card (bare mode: KPI tiles + per-service blocks; the RFI split lives in ③). */}
      <SectionCard title={<SecTitle n={2}>Sales Leader&rsquo;s request</SecTitle>}>
        <ProspectAndCommercialSummary version={version} proposalRaw={deal} showRfi={false} bare />
      </SectionCard>

      {/* ── ③ Order-level data (RFI) — a normal card like every other section; long
          matrices cap at 6 rows behind each grid's own Show more toggle instead. ── */}
      <SectionCard title={<SecTitle n={3}>Order-level data (RFI)</SecTitle>} accent="#AEB8C7">
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Order-level file:</Typography>
          {orderDataFileUri
            ? <Link component="button" underline="hover" sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }} onClick={() => openS3Doc(orderDataFileUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Open uploaded order-level data →</Link>
            : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No order-level file uploaded for this deal.</Typography>}
        </Stack>
        {!!pickupRows.length && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>Pickup locations</Typography>
            {miniGrid(pickupCols, pickupRows, (r) => r._k)}
          </Box>
        )}
        {rfiMatrices.length ? rfiMatrices.map((m) => <RfiMatrix key={m.serviceType} matrix={m} />) : (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No RFI split captured on this version.</Typography>
        )}
      </SectionCard>

      {/* ── Standard (GTM) cards — pre-built, handed to the client as-is ─── */}
      {!!standardCardRows.length && (
        <SectionCard title="Standard rate cards (GTM)" subtitle="Pre-built library cards sent to the client as-is — no pricing computed for these services.">
          <Stack spacing={1}>
            {standardCardRows.map((c) => (
              <Box key={c.serviceType} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', border: '1px solid', borderColor: 'divider', borderRadius: '8px', p: 1.25 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{serviceLabel(c.serviceType)}</Typography>
                <Pill label={c.rateCardType === 'COURIER_WISE' ? 'Courier-wise' : 'Blended'} styleKey={c.rateCardType === 'COURIER_WISE' ? 'purple' : 'neutral'} />
                <Pill label={c.prospectTier === 'ENTERPRISE' ? 'Enterprise' : 'SME'} styleKey={c.prospectTier === 'ENTERPRISE' ? 'orange' : 'neutral'} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{c.name}</Typography>
                {c.exact === false && <Pill label="closest match" styleKey="returned" />}
                <Box sx={{ flex: 1 }} />
                {c.fileUri && <Link component="button" underline="hover" sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }} onClick={() => openS3Doc(c.fileUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Open card →</Link>}
              </Box>
            ))}
          </Stack>
        </SectionCard>
      )}

      {/* ── ④ Trail — versions & the approval chain, nothing else. Files got their
          own ⑤ so the two content types never stack into one cluttered card. ── */}
      <SectionCard
        title={<SecTitle n={4}>Versions &amp; approvals</SecTitle>}
        accent="#AEB8C7"
        action={
          <Button size="small" variant={autoRefresh ? 'contained' : 'outlined'} sx={btn} onClick={() => setAutoRefresh((v) => !v)}>
            🕘 Auto-refresh log{autoRefresh ? ' · On' : ' · Off'}
          </Button>
        }
      >
        {/* Current version row gets a soft blue wash so it stands out from the history. */}
        <ProzoDataGrid
          flat={false} columns={versionCols} rows={threadRows} getRowId={(r) => r.id || r.v}
          autoHeight hideFooter paginationMode="client" rowCount={threadRows.length}
          getRowClassName={({ row }) => (row.current ? 'row-current' : '')}
          sx={{ '& .row-current': { bgcolor: '#F3F7FE' }, '& .row-current:hover': { bgcolor: '#EAF1FC' } }}
        />
        <Accordion elevation={0} sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '8px', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Approval chain (this version)</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <GapNote id="audit_from_chain" />
            {miniGrid(auditCols, auditRows, (r) => r._k)}
          </AccordionDetails>
        </Accordion>
      </SectionCard>

      {/* ── ⑤ Working files — real client-side .xlsx exports from computedLanes
          (+ the RTO breakout as a second Margin Working tab), slim rows. ────── */}
      <SectionCard title={<SecTitle n={5}>Working files</SecTitle>} accent="#AEB8C7">
        <WorkingFilesCard
          bare
          versionLabel={version?.seq ? `v${version.seq} (current)` : 'current'}
          lanes={version?.computedLanes || []}
          rtoLanes={version?.computedRtoLanes || []}
          docUri={version?.clientProposalUri}
          versionId={versionId}
          brand={brand || version?.dealId || 'proposal'}
          onCounterUi={() => setCounter({ open: true, mode: 'ui' })}
          onUploadCounter={() => setCounter({ open: true, mode: 'upload' })}
        />
      </SectionCard>

      {/* ── ⑥ Discussion — comments & attachments (v2 §1), real, deal-scoped ─────── */}
      <SectionCard title={<SecTitle n={6}>Discussion</SecTitle>} subtitle="Visible to SL + L1 + FH." accent="#AEB8C7">
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {comments.length === 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No comments yet.</Typography>}
          {comments.map((c) => (
            <Box key={c.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', px: 1.75, py: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{c.authorRole || '—'}</Typography>
                {c.kind === 'risk-analysis' && <Pill label="Risk analysis" styleKey="returned" />}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{c.createdAt ? new Date(c.createdAt).toLocaleString('en-GB') : ''}</Typography>
              </Stack>
              <Typography sx={{ fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.text}</Typography>
              {(c.attachments || []).map((a) => (
                <Link key={a.uri} component="button" underline="hover" sx={{ fontSize: 12, display: 'inline-block', mt: 0.5, mr: 1.5 }} onClick={() => openS3Doc(a.uri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>📎 {a.name}</Link>
              ))}
            </Box>
          ))}
        </Stack>
        <TextField
          fullWidth multiline minRows={2} size="small" placeholder="Type your comment…"
          value={cText} onChange={(e) => setCText(e.target.value)}
          required error={cTextErr}
          {...helperProps(cTextErr ? 'Comment text is required.' : undefined)}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size="small" value={cKind} onChange={(e) => setCKind(e.target.value)} sx={{ width: 160 }}>
            <MenuItem value="comment">Comment</MenuItem>
            <MenuItem value="risk-analysis">Risk analysis</MenuItem>
          </TextField>
          <Button size="small" variant="outlined" component="label" sx={btn}>
            {cFile ? `📎 ${cFile.name}` : '📎 Attach file'}
            <input type="file" hidden onChange={(e) => setCFile(e.target.files?.[0] || null)} />
          </Button>
          <Button size="small" variant="contained" sx={btn} disabled={commentBusy} onClick={doPostComment}>{commentBusy ? 'Posting…' : 'Post comment'}</Button>
        </Stack>
      </SectionCard>

      <CounterEditorDialog
        open={counter.open} mode={counter.mode} role={role} sections={sectionDefs}
        lanes={version?.computedLanes || []} rtoLanes={version?.computedRtoLanes || []} route={version?.route ?? null}
        cardType={version?.rateCardMode}
        submitting={counterState.isLoading}
        onSubmit={submitCounter}
        onClose={() => setCounter({ ...counter, open: false })}
      />
      <ReturnDeclineDialog
        open={rd.open} variant={rd.variant}
        onSubmit={submitRd} submitting={rdBusy}
        onClose={() => setRd({ ...rd, open: false })}
      />
    </Box>
  )
}

const btn = { textTransform: 'none', borderRadius: '6px', fontSize: 14 }
